<template>
        <div class="row">
          <div class="col-md-4" :class="{ 'col-md-12' : fullWidth }">
            <fieldset>
              <legend>Load Summary</legend>
              <div class="form-group-outer">
                <div class="form-group float-label-control" :class="{ 'has-error': errors.has('date') }">
                  <label for="" class="select-label">
                    Date
                    <template v-if="commodityChargeIsDelivery">Delivered</template>
                    <template v-else>Picked Up</template>
                  </label>
                  <date-range
                    :disabled="cannotModifyWeighbridgeDate"
                    :max-date="todaysDate"
                    v-model:date="date"
                    name="date"
                    :single-date-picker="true"
                    opens="right"
                  >
                  </date-range>
                </div>
              </div>
              <div class="form-group-outer">
                <div class="form-group float-label-control">
                  <label for="" class="select-label">
                    Total
                    <template v-if="commodityChargeIsDelivery">Delivered</template>
                    <template v-else>Picked Up</template>
                  </label>
                  <div class="input-group">
                    <input type="text" class="form-control" disabled :value="totalTonnageOfChargeType">
                    <span class="input-group-addon">tonne</span>
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
          <div class="col-md-8" :class="{ 'col-md-12' : fullWidth }">
            <ul class="nav nav-tabs m-b-md">
              <li :class="{ 'active': commodityChargeIsDelivery }">
                <a data-toggle="tab" data-target="#delivery">Delivery ({{ numberOfDeliveryDockets }})</a>
              </li>
              <li :class="{ 'active': commodityChargeIsPickup }">
                <a data-toggle="tab" data-target="#pickup">Pickup ({{ numberOfPickupDockets }})</a>
              </li>
              <li><a data-toggle="tab" data-target="#connote">Con-Note ({{ numberOfConsignmentNotes }})</a></li>
            </ul>

            <div class="tab-content">
              <div class="tab-pane" :class="{ 'active': commodityChargeIsDelivery }" id="delivery">
                <weighbridge-docket-item
                  v-for="(docket, index) in dockets.delivery"
                  :key="docket.index"
                  :index="index"
                  :docket="docket"
                  type="Delivery">
                </weighbridge-docket-item>

                <div class="text-center text-muted" v-if="haveNoDeliveryDockets">
                  No delivery dockets.
                </div>

                <div class="margin-20"></div>
                <a v-if="canAddNewDockets" @click="handleAddNewDeliveryDocket" class="btn btn-info btn-xs m-b-sm">
                  <i class="fa fa-plus"></i> Add Docket
                </a>
              </div>
              <div class="tab-pane" :class="{ 'active': commodityChargeIsPickup }" id="pickup">
                <weighbridge-docket-item
                  v-for="(docket, index) in dockets.pickup"
                  :key="docket.index"
                  :index="index"
                  :docket="docket"
                  type="Pickup">
                </weighbridge-docket-item>

                <div class="text-center text-muted" v-if="haveNoPickupDockets">
                  No pickup dockets.
                </div>

                <div class="margin-20"></div>
                <a v-if="canAddNewDockets" @click="handleAddNewPickupDocket" class="btn btn-info btn-xs m-b-sm">
                  <i class="fa fa-plus"></i> Add Docket
                </a>
              </div>
              <div class="tab-pane" id="connote">
                <weighbridge-consignment-item
                  v-for="(docket, index) in dockets.consignment"
                  :key="docket.index"
                  :index="index"
                  :docket="docket">
                </weighbridge-consignment-item>

                <div class="text-center text-muted" v-if="haveNoConsignmentNotes">
                  No consignment notes.
                </div>

                <div class="margin-20"></div>
                <a v-if="canAddNewDockets" @click="handleAddNewConNote" class="btn btn-info btn-xs m-b-sm">
                  <i class="fa fa-plus"></i> Add Con-Note
                </a>
              </div>
            </div>

            <template v-if="canStoreOrUpdateWeighbridgeDetails">
              <div class="margin-20"></div>

              <div class="row">
                <div class="col-sm-12">
                  <div class="text-right">
                    <ladda-button :on-click="handleSubmitWeighbridge" :max-width="fullWidth" type="success" size="small" class="m-b-md">
                      Submit Weighbridge Data
                    </ladda-button>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

</template>

<script>
  import { mapState, mapMutations, mapActions } from 'vuex'
  import {
    RESET_WEIGHBRIDGE, SET_WEIGHBRIDGE, SET_WEIGHBRIDGE_ATTRIBUTE,
    SET_DOCKET_ATTRIBUTE, ADD_DOCKET, REMOVE_DOCKET }
  from 'vx/mutations'
  import { initialDocketState } from 'vx/modules/weighbridge/docket'
  import { filter, merge, sumBy, map, each, isString } from 'lodash'
  import { DateRange, LaddaButton } from 'components/forms'
  import { notify, alert } from 'utils'
  import { jobHelpers } from 'mixins/jobs'
  import WeighbridgeDocketItem from './WeighbridgeDocketItem'
  import WeighbridgeConsignmentItem from './WeighbridgeConsignmentItem'
  import accounting from 'accounting'
  import moment from 'moment'

  export default {
    props: {
      fullWidth: {
        required: false,
        default: false
      },
      storedCallback: {
        required: false
      }
    },

    mixins: [jobHelpers],

    components: {
      WeighbridgeDocketItem,
      WeighbridgeConsignmentItem,
      DateRange,
      LaddaButton
    },

    data () {
      return {
        processing: false
      }
    },

    computed: {
      ...mapState('load', ['load']),
      ...mapState('weighbridge', ['weighbridge']),

      /**
       * Return todays date as a moment object.
       *
       * @return object
       */
      todaysDate () {
        return moment()
      },

      date: {
        get () {
          return this.weighbridge.date
        },

        set (value) {
          this[SET_WEIGHBRIDGE_ATTRIBUTE]({ attribute: 'date', value })
        }
      },

      /**
       * Determine if we can modify the weighbridges date. If we do not have a
       * weighbridge or we are an admin then this is possible.
       *
       * @return boolean
       */
      cannotModifyWeighbridgeDate () {
        if (this.jobIsEditable && (!this.haveWeighbridge || this.$user.isAdmin)) {
          return false
        }

        return true
      },

      /**
       * Determine if we can add new dockets. If the job haven't been confirmed or
       * we are an admin then this is possible.
       *
       * @return boolean
       */
      canAddNewDockets () {
        return this.jobIsEditable || this.$user.isAdmin
      },

      /**
       * Determine if we can store or update weighbridge details.
       *
       * If there is no weighbridge yet for this load then carrier/admins both have the
       * ability to store new details.
       *
       * Once we have a weighbridge then we can only save changes as a carrier if we
       * have at least one docket that has the status of "resubmit".
       *
       * @return boolean
       */
      canStoreOrUpdateWeighbridgeDetails () {
        if (this.processing === true) {
          return true
        }

        if (this.jobIsEditable && (!this.haveWeighbridge || this.$user.isAdmin)) {
          return true
        }

        return filter(this.weighbridge.dockets, docket => docket.status === 'resubmit').length > 0
      },

      /**
       * Determine if we have a weighbridge.
       *
       * @return boolean
       */
      haveWeighbridge () {
        return this.weighbridge.id !== null
      },

      /**
       * Total tonnage of the commodity charge type.
       *
       * @return string
       */
      totalTonnageOfChargeType () {
        if (this.load.commodity) {
          return accounting.formatNumber(sumBy(this.dockets[this.load.commodity.charge_type], docket => (parseFloat(docket.weight) || 0)), 2)
        }

        return 0
      },

      /**
       * Determine if commodity charge type is pickup.
       *
       * @return boolean
       */
      commodityChargeIsPickup () {
        return this.load.commodity && this.load.commodity.charge_type === 'pickup'
      },

      /**
       * Determine if commodity charge type is delivery.
       *
       * @return boolean
       */
      commodityChargeIsDelivery () {
        return this.load.commodity && this.load.commodity.charge_type === 'delivery'
      },

      /**
       * Determine if we have no completed delivery dockets.
       *
       * @return boolean
       */
      haveNoCompleteDeliveryDockets () {
        if (this.haveNoDeliveryDockets) {
          return true
        }

        return filter(this.dockets.delivery, this.validateCompleteDocket).length === 0
      },

      /**
       * Determine if we have no completed pickup dockets.
       *
       * @return boolean
       */
      haveNoCompletePickupDockets () {
        if (this.haveNoPickupDockets) {
          return true
        }

        return filter(this.dockets.pickup, this.validateCompleteDocket).length === 0
      },

      /**
       * Determine if we have no pickup dockets.
       *
       * @return boolean
       */
      haveNoPickupDockets () {
        return this.numberOfPickupDockets === 0
      },

      /**
       * Determine if we have no delivery dockets.
       *
       * @return boolean
       */
      haveNoDeliveryDockets () {
        return this.numberOfDeliveryDockets === 0
      },

      /**
       * Determine if we have no consignment notes.
       *
       * @return boolean
       */
      haveNoConsignmentNotes () {
        return this.numberOfConsignmentNotes === 0
      },

      /**
       * Number of delivery dockets.
       *
       * @return integer
       */
      numberOfDeliveryDockets () {
        return this.dockets.delivery.length
      },

      /**
       * Number of pickup dockets.
       *
       * @return integer
       */
      numberOfPickupDockets () {
        return this.dockets.pickup.length
      },

      /**
       * Number of consignment notes.
       *
       * @return integer
       */
      numberOfConsignmentNotes () {
        return this.dockets.consignment.length
      },

      /**
       * Get the dockets for the weighbridge. We need to map the dockets index onto each docket
       * as that is how we identify the dockets within the weighbridges state of dockets.
       *
       * Whenever the underlying dockets change we need to update this as a docket may be moved
       * or removed which requires us to update its index accordingly.
       *
       * @return object
       */
      dockets () {
        each(this.weighbridge.dockets, (docket, index) => {
          this[SET_DOCKET_ATTRIBUTE]({ index, attribute: 'index', value: index })
        })

        return {
          delivery: filter(this.weighbridge.dockets, docket => docket.type === 'delivery'),
          pickup: filter(this.weighbridge.dockets, docket => docket.type === 'pickup'),
          consignment: filter(this.weighbridge.dockets, docket => docket.type === 'connote')
        }
      },

      /**
       * The new docket index.
       *
       * @return integer
       */
      newDocketIndex () {
        return this.weighbridge.dockets.length
      }
    },

    created () {
      if (this.load.weighbridge) {
        this[SET_WEIGHBRIDGE](this.load.weighbridge)
      } else {
        this[RESET_WEIGHBRIDGE]()
      }
    },

    methods: {
      ...mapMutations('weighbridge', [
        RESET_WEIGHBRIDGE,
        SET_WEIGHBRIDGE,
        SET_WEIGHBRIDGE_ATTRIBUTE,
        SET_DOCKET_ATTRIBUTE,
        ADD_DOCKET,
        REMOVE_DOCKET
      ]),
      ...mapActions('weighbridge', ['storeWeighbridge', 'updateWeighbridge']),
      ...mapActions('weighbridge/docket', ['storeDocket', 'updateDocket']),
      ...mapActions('load', ['getLoad']),

      addEmptyDeliveryDocket () {
        this[ADD_DOCKET](initialDocketState({ type: 'delivery', index: this.newDocketIndex }))
      },

      addEmptyPickupDocket () {
        this[ADD_DOCKET](initialDocketState({ type: 'pickup', index: this.newDocketIndex }))
      },

      addEmptyConNote () {
        this[ADD_DOCKET](initialDocketState({ type: 'connote', index: this.newDocketIndex }))
      },

      handleAddNewDeliveryDocket () {
        this.addEmptyDeliveryDocket()
      },

      handleAddNewPickupDocket () {
        this.addEmptyPickupDocket()
      },

      handleAddNewConNote () {
        this.addEmptyConNote()
      },

      handleSubmitWeighbridge () {
        this.processing = true

        // Validate that we have at least one fully completed docket for the given commodity
        // charge type. Reject the promise chain if we do not.
        /*
        const validateWeHaveRequiredDocket = () => {
          return new Promise((resolve, reject) => {
            if (this.commodityChargeIsDelivery && this.haveNoCompleteDeliveryDockets) {
              reject('You must have at least one completed delivery docket.')
            } else if (this.commodityChargeIsPickup && this.haveNoCompletePickupDockets) {
              reject('You must have at least one completed pickup docket.')
            } else {
              resolve()
            }
          })
        }
        */

        const handleValidationErrors = (error) => {
          if (isString(error)) {
            notify.error(error)
          }

          console.log(error)
        }

        // Store or update all the dockets associated with the weighbridge.
        const storeOrUpdateDockets = () => {
          return Promise.all(map(this.weighbridge.dockets, (docket) => {
            // Updating a new dockets state involves merging the original index from our
            // docket onto the response data. This way we can replace the docket at its
            // existing index so we don't get weird shifting of docket as we save.
            const updateDocketState = (response) => {
              this[ADD_DOCKET](merge(response.data, { index: docket.index }))

              return response
            }

            if (docket.id === null) {
              return this.storeDocket({ weighbridge: this.weighbridge.id, docket }).then(updateDocketState)
            } else {
              return this.updateDocket({ id: docket.id, docket }).then(updateDocketState)
            }
          }))
        }

        // Store or update the weighbridge itself before we can move on to storing or updating
        // the dockets. This sends the dockets along to be validated so we don't create a
        // weighbridge without first determining if the dockets are okay.
        const storeOrUpdateWeighbridge = () => {
          let payload = {
            date: this.date,
            dockets: map(this.weighbridge.dockets, docket => {
              return {
                type: docket.type,
                number: docket.number,
                weight: docket.weight,
                file: docket.photo_name
              }
            })
          }

          if (this.weighbridge.id === null) {
            return this.storeWeighbridge({ load: this.load.id, payload }).then(({ data: weighbridge }) => {
              this[SET_WEIGHBRIDGE_ATTRIBUTE]({ attribute: 'id', value: weighbridge.id })
            })
          } else if (this.$user.isAdminOrCoordinator) {
            return this.updateWeighbridge({ id: this.weighbridge.id, payload })
          } else {
            return Promise.resolve()
          }
        }

        // If the user is a carrier confirm they wish to perform the given action as they
        // will not be able to alter the details of the weighbridge. We only confirm if
        // this is the first time they are submitting the details for the weighbridge.
        const confirmCarrierAction = () => {
          if (this.$user.isCarrier && !this.haveWeighbridge) {
            return alert.confirm({ text: 'Once submitted you will not be able to alter these details.' })
              .catch(() => Promise.reject())
          }

          return Promise.resolve()
        }

        const updateLoadState = () => {
          if (!this.storedCallback) {
            return this.getLoad(this.load.id)
          }
        }

        const fireStoredCallback = () => {
          if (this.storedCallback) {
            this.storedCallback()
          }
        }

        return this.validateAll()
          // http://support.codium.com.au/a/tickets/9649
          // bypass docket validation
          // .then(validateWeHaveRequiredDocket)
          .then(confirmCarrierAction)
          .then(storeOrUpdateWeighbridge)
          .then(storeOrUpdateDockets)
          .then(updateLoadState)
          .then(() => { this.processing = false })
          .then(fireStoredCallback)
          .catch(handleValidationErrors)
      },

      /**
       * Validate that a docket is completed.
       *
       * @param object docket
       *
       * @return boolean
       */
      validateCompleteDocket (docket) {
        if (docket.number === null || docket.number === '' || docket.weight === null || docket.weight === '' || docket.photo_name === null) {
          return false
        }

        return true
      }
    }
  }
</script>



// WEBPACK FOOTER //
// WeighbridgeDetails.vue?6a0bc11e